import React, { useState, useEffect, useCallback } from 'react';
import {
  tokens,
  makeStyles,
  Button,
  Text,
  Spinner,
  Avatar,
  Caption1,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Label,
  Textarea,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  mergeClasses,
  Input,
  Divider,
  Badge,
} from '@fluentui/react-components';
import {
  DeleteRegular,
  EditRegular,
  MoreVerticalRegular,
  AddRegular,
  BroomRegular,
  ChatRegular,
  SearchRegular,
  DismissRegular,
  ImageRegular,
  ShieldCheckmarkRegular,
} from '@fluentui/react-icons';
import { AccountInfo } from '@azure/msal-browser';
import { Agent, Conversation } from '../types';

const useStyles = makeStyles({
  sidebar: {
    width: '320px',
    display: 'flex',
    flexDirection: 'column',
    background: tokens.colorNeutralBackground3,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow4,
    zIndex: 2,
    overflowY: 'auto',
    flexShrink: 0,
    transition: 'transform 0.3s ease-in-out, width 0.3s ease-in-out',
    '@media (max-width: 768px)': {
      position: 'fixed',
      left: 0,
      bottom: 0,
      width: '280px',
      zIndex: 1000,
      transform: 'translateX(-100%)',
      borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
      boxShadow: tokens.shadow16,
    },
  },
  sidebarMobileOpen: {
    '@media (max-width: 768px)': {
      transform: 'translateX(0%)',
    },
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1,
  },
  sidebarTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  agentSearch: {
    padding: `0 ${tokens.spacingHorizontalM} ${tokens.spacingVerticalS}`,
    flexShrink: 0,
  },
  agentList: {
    overflowY: 'auto',
    padding: `0 ${tokens.spacingHorizontalM} ${tokens.spacingHorizontalM}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  agentCard: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    border: '1px solid transparent',
    transition: 'background 0.15s, border-color 0.15s',
    minHeight: '48px',
    ':hover': {
      background: tokens.colorNeutralBackground1Hover,
      borderColor: tokens.colorNeutralStroke1,
    } as Record<string, string>,
  },
  activeAgentCard: {
    background: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },
  agentCardInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1,
    gap: '1px',
  },
  agentCardName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  agentCardDesc: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  loadingSpinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: tokens.spacingVerticalXXL,
  },
  errorMessage: {
    margin: tokens.spacingHorizontalL,
  },
  dialogInput: {
    width: '100%',
  },
  noAgentsText: {
    padding: tokens.spacingVerticalL,
  },
  conversationSection: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowY: 'auto',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  conversationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    flexShrink: 0,
  },
  conversationSearch: {
    padding: `0 ${tokens.spacingHorizontalM} ${tokens.spacingVerticalS}`,
    flexShrink: 0,
  },
  conversationList: {
    overflowY: 'auto',
    padding: `0 ${tokens.spacingHorizontalM}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  conversationItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    transition: 'background 0.15s',
    ':hover': {
      background: tokens.colorNeutralBackground1,
    } as Record<string, string>,
  },
  activeConversationItem: {
    background: tokens.colorBrandBackground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  conversationItemText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1,
    gap: '2px',
  },
  conversationTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  conversationDeleteBtn: {
    opacity: 0,
    transition: 'opacity 0.15s',
    minWidth: 'auto',
    minHeight: 'auto',
    padding: '2px',
  },
  conversationItemHover: {
    ':hover .convDelete': { opacity: '1' } as Record<string, string>,
  },
  emptyConversations: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    textAlign: 'center',
  },
  paceAgentsSection: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  paceAgentsSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
  },
  paceAgentsList: {
    padding: `0 ${tokens.spacingHorizontalM} ${tokens.spacingHorizontalM}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  paceAgentCard: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    border: '1px solid transparent',
    transition: 'background 0.15s, border-color 0.15s',
    minHeight: '48px',
    ':hover': {
      background: tokens.colorNeutralBackground1Hover,
      borderColor: tokens.colorNeutralStroke1,
    } as Record<string, string>,
  },
  activePaceAgentCard: {
    background: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },
});

// --- Helpers ---

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// --- PaceAgentCard (read-only, no edit/delete) ---

interface PaceAgentCardProps {
  agent: Agent;
  isActive: boolean;
  onSelect: (id: string) => void;
}

const PaceAgentCard: React.FC<PaceAgentCardProps> = ({ agent, isActive, onSelect }) => {
  const styles = useStyles();
  const agentName = agent.name || `Agent ${agent.id.substring(0, 6)}`;

  return (
    <div
      className={mergeClasses(styles.paceAgentCard, isActive && styles.activePaceAgentCard)}
      onClick={() => onSelect(agent.id)}
      role="button"
      aria-label={`Select Pace Agent ${agentName}`}
    >
      <Avatar name={agentName} color="brand" size={32} />
      <div className={styles.agentCardInfo}>
        <span className={styles.agentCardName}>{agentName}</span>
        {agent.description && (
          <span className={styles.agentCardDesc}>{agent.description}</span>
        )}
      </div>
    </div>
  );
};

// --- AgentCard ---

interface AgentCardProps {
  agent: Agent;
  isActive: boolean;
  onSelect: (id: string) => void;
  onEdit: (agent: Agent) => void;
  onClear: (id: string) => void;
  onDelete: (id: string) => void;
  onRegenerateAvatar: (agent: Agent) => void;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent, isActive, onSelect, onEdit, onClear, onDelete, onRegenerateAvatar }) => {
  const styles = useStyles();
  const agentName = agent.name || `Agent ${agent.id.substring(0, 6)}`;
  const agentDescription = agent.description || '';

  const avatarProps = agent._pendingAvatar
    ? { icon: <Spinner size="tiny" />, color: 'brand' as 'brand' }
    : agent.avatar
      ? { image: { src: agent.avatar } }
      : { name: agentName, color: 'brand' as 'brand' };

  return (
    <div
      className={mergeClasses(styles.agentCard, isActive && styles.activeAgentCard)}
      onClick={() => onSelect(agent.id)}
      role="button"
      aria-label={`Select agent ${agentName}`}
    >
      <Avatar {...avatarProps} size={32} />
      <div className={styles.agentCardInfo}>
        <span className={styles.agentCardName}>{agentName}</span>
        {agentDescription && (
          <span className={styles.agentCardDesc}>{agentDescription}</span>
        )}
      </div>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button
            icon={<MoreVerticalRegular />}
            appearance="transparent"
            size="small"
            aria-label="Agent Actions"
            onClick={(e) => e.stopPropagation()}
            style={{ minWidth: 'auto', padding: '2px', flexShrink: 0 }}
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<EditRegular />} onClick={(e) => { e.stopPropagation(); onEdit(agent); }}>Edit Agent</MenuItem>
            <MenuItem icon={<BroomRegular />} onClick={(e) => { e.stopPropagation(); onClear(agent.id); }}>Clear Chat History</MenuItem>
            <MenuItem icon={<DeleteRegular />} onClick={(e) => { e.stopPropagation(); onDelete(agent.id); }}>Delete Agent</MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
};

// --- Sidebar ---

interface SidebarProps {
  activeAgentId: string | null;
  account: AccountInfo | null;
  agents: Agent[];
  setAgents: React.Dispatch<React.SetStateAction<Agent[]>>;
  fetchAgents: () => Promise<void>;
  isLoadingAgents: boolean;
  agentsError: string | null;
  handleSelectAgent: (id: string | null) => void;
  isMobileView: boolean;
  isMobileSidebarOpen: boolean;
  headerHeight: string;
  selectedModel: string;
  conversations: Conversation[];
  activeConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onDeleteConversation: (id: string) => void;
  onNewConversation: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeAgentId, account, agents, setAgents, fetchAgents,
  isLoadingAgents, agentsError, handleSelectAgent,
  isMobileView, isMobileSidebarOpen, headerHeight, selectedModel,
  conversations, activeConversationId, onSelectConversation, onDeleteConversation, onNewConversation,
}) => {
  const styles = useStyles();
  const [isCreateAgentDialogOpen, setIsCreateAgentDialogOpen] = useState(false);
  const [isEditAgentDialogOpen, setIsEditAgentDialogOpen] = useState(false);
  const [agentToEdit, setAgentToEdit] = useState<Agent | null>(null);
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentDescription, setNewAgentDescription] = useState('');
  const [newAgentInstructions, setNewAgentInstructions] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const paceChatCreatedRef = React.useRef(false);
  const hasFetchedRef = React.useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [agentSearchQuery, setAgentSearchQuery] = useState('');

  // Track when initial fetch completes
  useEffect(() => {
    if (!isLoadingAgents && agents.length >= 0) {
      hasFetchedRef.current = true;
    }
  }, [isLoadingAgents, agents.length]);

  // Poll for avatar after creation
  const pollForAvatar = useCallback(async (agentId: string) => {
    for (let i = 0; i < 10; i++) {
      await new Promise(resolve => setTimeout(resolve, 5000));
      try {
        const res = await fetch(`/api/agents/${agentId}`);
        if (res.ok) {
          const agent = await res.json();
          if (agent.avatarURL) {
            await fetchAgents();
            return;
          }
        }
      } catch { /* continue polling */ }
    }
  }, [fetchAgents]);

  // Auto-create "Pace Chat" default agent if it doesn't exist
  useEffect(() => {
    if (paceChatCreatedRef.current || isLoadingAgents || !hasFetchedRef.current || !account) return;
    const hasPaceChat = agents.some(a => a.name === 'Pace Chat');
    if (!hasPaceChat) {
      paceChatCreatedRef.current = true;
      console.log('[Sidebar] Auto-creating Pace Chat agent');
      fetch('/api/agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Pace Chat',
          description: 'General purpose AI assistant',
          instructions: 'You are a helpful, knowledgeable AI assistant for Pace Applied Solutions. Assist users with questions, analysis, writing, and problem-solving.',
        }),
      })
        .then(res => {
          if (!res.ok) throw new Error('Failed to create Pace Chat agent');
          return res.json();
        })
        .then(created => {
          // Trigger avatar generation in background
          fetch('/api/agent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: 'Pace Chat', description: 'General purpose AI assistant', id: created.id }),
          }).catch(err => console.warn('Avatar generation failed:', err));
          // Poll for avatar
          pollForAvatar(created.id);
          return fetchAgents();
        })
        .catch(err => {
          console.error('Failed to auto-create Pace Chat agent:', err);
          paceChatCreatedRef.current = false;
        });
    }
  }, [agents, isLoadingAgents, account, fetchAgents, pollForAvatar]);

  // Auto-select Pace Chat on first load if no agent is active
  useEffect(() => {
    if (!activeAgentId && agents.length > 0 && !isLoadingAgents) {
      const paceChat = agents.find(a => a.name === 'Pace Chat');
      if (paceChat) handleSelectAgent(paceChat.id);
    }
  }, [agents, activeAgentId, isLoadingAgents, handleSelectAgent]);

  const handleOpenCreateAgentDialog = () => {
    setNewAgentName('');
    setNewAgentDescription('');
    setNewAgentInstructions('');
    setIsCreateAgentDialogOpen(true);
  };

  const handleCreateAgent = useCallback(async () => {
    if (!newAgentName.trim()) {
      alert('Agent name is required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newAgentName,
          description: newAgentDescription,
          instructions: newAgentInstructions,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errData.error || 'Failed to create agent');
      }
      const created = await res.json();

      // Trigger avatar generation in background
      fetch('/api/agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newAgentName, description: newAgentDescription, id: created.id }),
      }).catch(err => console.warn('Avatar generation failed:', err));

      // Mark as pending avatar and poll
      setAgents(prev => prev.map(a => a.id === created.id ? { ...a, _pendingAvatar: true } : a));
      pollForAvatar(created.id);

      setIsCreateAgentDialogOpen(false);
      await fetchAgents();
    } catch (err: any) {
      console.error('Error creating agent:', err);
      alert(`Failed to create agent: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [newAgentName, newAgentDescription, newAgentInstructions, fetchAgents, pollForAvatar, setAgents]);

  const handleOpenEditAgentDialog = (agent: Agent) => {
    setAgentToEdit(agent);
    setNewAgentName(agent.name || '');
    setNewAgentDescription(agent.description || '');
    setNewAgentInstructions(agent.instructions || '');
    setIsEditAgentDialogOpen(true);
  };

  const handleUpdateAgent = useCallback(async () => {
    if (!agentToEdit || !newAgentName.trim()) {
      alert('Agent name is required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`/api/agents/${agentToEdit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newAgentName,
          description: newAgentDescription,
          instructions: newAgentInstructions,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errData.error || 'Failed to update agent');
      }
      setIsEditAgentDialogOpen(false);
      setAgentToEdit(null);
      await fetchAgents();
    } catch (err: any) {
      console.error('Error updating agent:', err);
      alert(`Failed to update agent: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [agentToEdit, newAgentName, newAgentDescription, newAgentInstructions, fetchAgents]);

  const handleDeleteAgent = useCallback(async (id: string) => {
    const agentName = agents.find(a => a.id === id)?.name;
    if (!window.confirm(`Are you sure you want to delete agent ${agentName}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/agents/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete agent');
      if (activeAgentId === id) handleSelectAgent(null);
      await fetchAgents();
    } catch (err: any) {
      console.error('Error deleting agent:', err);
      alert(`Failed to delete agent: ${err.message}`);
    }
  }, [agents, activeAgentId, handleSelectAgent, fetchAgents]);

  const handleClearChatHistory = (agentId: string) => {
    const agentName = agents.find(a => a.id === agentId)?.name;
    if (!window.confirm(`Clear chat history for ${agentName}? A new chat session will start.`)) return;
    handleSelectAgent(null);
    setTimeout(() => handleSelectAgent(agentId), 0);
  };

  const handleRegenerateAvatar = useCallback(async (agent: Agent) => {
    setAgents(prev => prev.map(a => a.id === agent.id ? { ...a, _pendingAvatar: true, avatar: undefined } : a));
    try {
      const res = await fetch('/api/agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: agent.name, description: agent.description || '', id: agent.id }),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }
      await fetchAgents();
    } catch (err: any) {
      console.error('Avatar generation failed:', err);
      alert(`Avatar generation failed: ${err.message}`);
      setAgents(prev => prev.map(a => a.id === agent.id ? { ...a, _pendingAvatar: false } : a));
    }
  }, [fetchAgents, setAgents]);

  // Filtered user agents (excluding Pace Agents)
  const filteredAgents = agents
    .filter(a => !a.isPaceAgent)
    .filter(a => !agentSearchQuery || a.name.toLowerCase().includes(agentSearchQuery.toLowerCase()) || (a.description || '').toLowerCase().includes(agentSearchQuery.toLowerCase()))
    .sort((a, b) => (a.name === 'Pace Chat' ? -1 : b.name === 'Pace Chat' ? 1 : 0));

  // Pace Agents (read-only, from Azure AI Foundry)
  const paceAgents = agents.filter(a => a.isPaceAgent);

  // Filtered conversations for active agent
  const agentConversations = conversations
    .filter(c => c.agentId === activeAgentId)
    .filter(c => !searchQuery || c.title.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 30);

  return (
    <div
      className={mergeClasses(styles.sidebar, isMobileView && isMobileSidebarOpen && styles.sidebarMobileOpen)}
      style={isMobileView ? { top: headerHeight } : {}}
    >
      <div className={styles.sidebarHeader}>
        <Text className={styles.sidebarTitle}>Agents</Text>
        <Tooltip content="Create New Agent" relationship="label">
          <Button
            icon={<AddRegular />}
            appearance="primary"
            onClick={handleOpenCreateAgentDialog}
            disabled={isLoadingAgents}
          />
        </Tooltip>
      </div>

      {isLoadingAgents && !agents.length && (
        <div className={styles.loadingSpinner}><Spinner label="Loading agents..." /></div>
      )}
      {agentsError && (
        <MessageBar intent="error" className={styles.errorMessage}>
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {agentsError}
          </MessageBarBody>
        </MessageBar>
      )}
      {!isLoadingAgents && !agentsError && agents.length === 0 && (
        <Text align="center" className={styles.noAgentsText}>No agents found. Create one to get started.</Text>
      )}

      {!agentsError && agents.length > 3 && (
        <div className={styles.agentSearch}>
          <Input
            placeholder="Search agents..."
            size="small"
            contentBefore={<SearchRegular />}
            value={agentSearchQuery}
            onChange={(_e, data) => setAgentSearchQuery(data.value)}
            style={{ width: '100%' }}
          />
        </div>
      )}

      {!agentsError && (
        <div className={styles.agentList}>
          {filteredAgents.map(agent => (
            <AgentCard
              key={agent.id}
              agent={agent}
              isActive={activeAgentId === agent.id}
              onSelect={handleSelectAgent}
              onEdit={handleOpenEditAgentDialog}
              onClear={handleClearChatHistory}
              onDelete={handleDeleteAgent}
              onRegenerateAvatar={handleRegenerateAvatar}
            />
          ))}
        </div>
      )}

      {/* Pace Agents (Azure AI Foundry — read-only) */}
      {paceAgents.length > 0 && (
        <div className={styles.paceAgentsSection}>
          <div className={styles.paceAgentsSectionHeader}>
            <ShieldCheckmarkRegular style={{ color: tokens.colorBrandForeground1, fontSize: 14 }} />
            <Text size={200} weight="semibold" style={{ color: tokens.colorBrandForeground1 }}>
              Pace Agents
            </Text>
            <Tooltip content="Official Pace AI agents — managed by administrators" relationship="label">
              <Badge appearance="tint" color="brand" size="small">Official</Badge>
            </Tooltip>
          </div>
          <div className={styles.paceAgentsList}>
            {paceAgents.map(agent => (
              <PaceAgentCard
                key={agent.id}
                agent={agent}
                isActive={activeAgentId === agent.id}
                onSelect={handleSelectAgent}
              />
            ))}
          </div>
        </div>
      )}

      {/* Conversation History */}
      {activeAgentId && (
        <div className={styles.conversationSection}>
          <Divider />
          <div className={styles.conversationHeader}>
            <Text size={200} weight="semibold" style={{ color: tokens.colorNeutralForeground2 }}>Conversations</Text>
            <Tooltip content="New Chat" relationship="label">
              <Button icon={<AddRegular />} appearance="subtle" size="small" onClick={onNewConversation} style={{ minWidth: 'auto', padding: '2px 6px' }} />
            </Tooltip>
          </div>

          {agentConversations.length > 3 && (
            <div className={styles.conversationSearch}>
              <Input
                placeholder="Search..."
                size="small"
                contentBefore={<SearchRegular />}
                value={searchQuery}
                onChange={(_e, data) => setSearchQuery(data.value)}
                style={{ width: '100%' }}
              />
            </div>
          )}

          <div className={styles.conversationList}>
            {agentConversations.length === 0 && (
              <div className={styles.emptyConversations}>
                <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>
                  <ChatRegular style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                  No conversations yet
                </Caption1>
              </div>
            )}
            {agentConversations.map(conv => (
              <div
                key={conv.id}
                className={mergeClasses(
                  styles.conversationItem,
                  conv.id === activeConversationId && styles.activeConversationItem,
                  styles.conversationItemHover,
                )}
                onClick={() => onSelectConversation(conv.id)}
              >
                <div className={styles.conversationItemText}>
                  <Text size={200} className={styles.conversationTitle}>{conv.title}</Text>
                  <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>
                    {formatRelativeTime(conv.updatedAt)}
                  </Caption1>
                </div>
                <Button
                  icon={<DismissRegular />}
                  appearance="transparent"
                  size="small"
                  className={`${styles.conversationDeleteBtn} convDelete`}
                  onClick={(e) => { e.stopPropagation(); onDeleteConversation(conv.id); }}
                  aria-label="Delete conversation"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Agent Dialog */}
      <Dialog open={isCreateAgentDialogOpen} onOpenChange={(_, data) => setIsCreateAgentDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Create New Agent</DialogTitle>
            <DialogContent>
              <Label htmlFor="agent-name" required>Agent Name</Label>
              <Input id="agent-name" value={newAgentName} onChange={(e: any, data: any) => setNewAgentName(data.value)} className={styles.dialogInput} />
              <br />
              <Label htmlFor="agent-desc">Description</Label>
              <Textarea id="agent-desc" value={newAgentDescription} onChange={(e, data) => setNewAgentDescription(data.value)} resize="vertical" className={styles.dialogInput} />
              <br />
              <Label htmlFor="agent-instructions">Instructions (System Prompt)</Label>
              <Textarea id="agent-instructions" value={newAgentInstructions} onChange={(e, data) => setNewAgentInstructions(data.value)} resize="vertical" rows={5} className={styles.dialogInput} />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleCreateAgent} disabled={isSubmitting || !newAgentName.trim()}>
                {isSubmitting ? <Spinner size="tiny" /> : "Create"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Edit Agent Dialog */}
      <Dialog open={isEditAgentDialogOpen} onOpenChange={(_, data) => setIsEditAgentDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Edit Agent: {agentToEdit?.name}</DialogTitle>
            <DialogContent>
              <Label htmlFor="edit-agent-name" required>Agent Name</Label>
              <Input id="edit-agent-name" value={newAgentName} onChange={(e: any, data: any) => setNewAgentName(data.value)} className={styles.dialogInput} />
              <br />
              <Label htmlFor="edit-agent-desc">Description</Label>
              <Textarea id="edit-agent-desc" value={newAgentDescription} onChange={(e, data) => setNewAgentDescription(data.value)} resize="vertical" className={styles.dialogInput} />
              <br />
              <Label htmlFor="edit-agent-instructions">Instructions (System Prompt)</Label>
              <Textarea id="edit-agent-instructions" value={newAgentInstructions} onChange={(e, data) => setNewAgentInstructions(data.value)} resize="vertical" rows={5} className={styles.dialogInput} />
              <br />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Label>Avatar</Label>
                {agentToEdit?.avatar && (
                  <Avatar image={{ src: agentToEdit.avatar }} size={32} />
                )}
                <Button
                  icon={<ImageRegular />}
                  appearance="subtle"
                  size="small"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (agentToEdit) {
                      handleRegenerateAvatar(agentToEdit);
                    }
                  }}
                >
                  {agentToEdit?.avatar ? 'Regenerate Avatar' : 'Generate Avatar'}
                </Button>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" onClick={() => setAgentToEdit(null)}>Cancel</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleUpdateAgent} disabled={isSubmitting || !newAgentName.trim()}>
                {isSubmitting ? <Spinner size="tiny" /> : "Update"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
